/**
 * useRaceSimulator Hook
 *
 * React hook for managing race simulation during demos.
 * Uses Edge Function for database operations.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { RaceSimulator } from '@/services/RaceSimulator';

interface UseRaceSimulatorOptions {
  raceRoomId: string | null;
  questionCount: number;
}

export function useRaceSimulator({ raceRoomId, questionCount }: UseRaceSimulatorOptions) {
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulatedCount, setSimulatedCount] = useState(0);
  const [isAddingBots, setIsAddingBots] = useState(false);
  const simulatorRef = useRef<RaceSimulator | null>(null);

  // Create simulator when room is available
  useEffect(() => {
    // Reset state when room changes
    setSimulatedCount(0);
    setIsSimulating(false);

    if (raceRoomId && questionCount > 0) {
      simulatorRef.current = new RaceSimulator(raceRoomId, questionCount);
    }
    return () => {
      simulatorRef.current?.cleanup();
      simulatorRef.current = null;
    };
  }, [raceRoomId, questionCount]);

  /**
   * Add simulated participants to the race
   */
  const addSimulatedParticipants = useCallback(async (count: number) => {
    if (!simulatorRef.current) return { success: false, error: 'No active room' };

    setIsAddingBots(true);
    const result = await simulatorRef.current.addParticipants(count);
    if (result.success) {
      setSimulatedCount((prev) => prev + count);
    }
    setIsAddingBots(false);
    return result;
  }, []);

  /**
   * Start the race simulation
   */
  const startSimulation = useCallback(() => {
    if (!simulatorRef.current) return;
    simulatorRef.current.startSimulation();
    setIsSimulating(true);
  }, []);

  /**
   * Stop the simulation
   */
  const stopSimulation = useCallback(() => {
    simulatorRef.current?.stopSimulation();
    setIsSimulating(false);
  }, []);

  /**
   * Clean up all simulated participants
   */
  const cleanupSimulation = useCallback(async () => {
    await simulatorRef.current?.cleanup();
    setSimulatedCount(0);
    setIsSimulating(false);
  }, []);

  return {
    isSimulating,
    simulatedCount,
    isAddingBots,
    addSimulatedParticipants,
    startSimulation,
    stopSimulation,
    cleanupSimulation,
  };
}
